import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/AuthContext';
import { stripeAPI, userAPI, getFullUrl } from '../services/api';

const ClientBilling = () => {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const res = await userAPI.getMyCoaches();
        setCoaches(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCoaches();
  }, []);

  const handlePathBCheckout = async (coachId) => {
    try {
      setActionLoading((prev) => ({ ...prev, [coachId]: true }));
      const res = await stripeAPI.checkoutPathB(coachId);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to start checkout. Please try again.';
      alert(msg);
    } finally {
      setActionLoading((prev) => ({ ...prev, [coachId]: false }));
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await stripeAPI.createPortalSession();
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      alert('Failed to open billing portal.');
    }
  };

  if (loading) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] overflow-x-hidden font-display flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-tangerine"></div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-[var(--bg-main)] overflow-x-hidden font-display text-[var(--text-primary)]">
      <div className="max-w-2xl mx-auto w-full px-4 py-10">

        <h1 className="text-2xl md:text-3xl font-bold mb-2">Billing & Subscription</h1>
        <p className="text-[var(--text-secondary)] text-sm mb-8">
          Manage your subscriptions with your coaches below.
        </p>

        {coaches.length === 0 ? (
          <div className="bg-[var(--surface-white)] rounded-3xl border border-[var(--border-light)] p-12 text-center shadow-xl shadow-black/5">
            <span className="text-4xl block mb-4">🧑‍💼</span>
            <p className="text-[var(--text-secondary)] font-medium">No coaches assigned yet.</p>
            <p className="text-xs text-[var(--text-secondary)]/60 mt-1">Ask your coach to send you an invite link.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {coaches.map((coach) => {
              // clientRelation fields come back from the coach object
              const isApproved = coach.isApproved;
              const path = coach.path;
              const price = coach.price;
              const status = coach.status;
              const currentPeriodEnd = coach.currentPeriodEnd;

              const needsPayment = isApproved && path === 'B' && status !== 'active';
              const isActive = status === 'active';

              return (
                <div
                  key={coach.id}
                  className="bg-[var(--surface-white)] rounded-3xl border border-[var(--border-light)] p-6 shadow-xl shadow-black/5"
                >
                  {/* Coach Header */}
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center text-2xl font-bold text-tangerine overflow-hidden border border-orange-500/20">
                      {coach.avatarUrl
                        ? <img src={getFullUrl(coach.avatarUrl)} alt={coach.name} className="w-full h-full object-cover" />
                        : (coach.name || '?').charAt(0).toUpperCase()
                      }
                    </div>
                    <div>
                      <p className="font-bold text-lg text-[var(--text-primary)]">{coach.name}</p>
                      <p className="text-sm text-[var(--text-secondary)]">{coach.email}</p>
                    </div>
                    <div className="ml-auto">
                      {isActive && (
                        <span className="text-xs font-bold text-green-500 bg-green-500/10 border border-green-500/20 px-3 py-1 rounded-full">
                          ✓ Active
                        </span>
                      )}
                      {!isApproved && (
                        <span className="text-xs font-bold text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 px-3 py-1 rounded-full">
                          ⏳ Awaiting Approval
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Subscription Details */}
                  {isApproved && (
                    <div className="rounded-2xl bg-[var(--off-white)] border border-[var(--border-light)] p-4 mb-5 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--text-secondary)]">Billing method</span>
                        <span className="font-bold text-[var(--text-primary)]">
                          {path === 'A' ? '🎁 Coach covers your access' : '💳 You pay directly'}
                        </span>
                      </div>
                      {path === 'B' && price && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--text-secondary)]">Monthly price</span>
                          <span className="font-bold text-tangerine">${price}/month</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--text-secondary)]">Status</span>
                        <span className={`font-bold ${isActive ? 'text-green-500' : 'text-[var(--text-secondary)]'}`}>
                          {isActive ? 'Active' : status === 'canceled' ? 'Canceled' : 'Inactive'}
                        </span>
                      </div>
                      {isActive && currentPeriodEnd && (
                        <div className="flex justify-between text-sm border-t border-[var(--border-light)]/40 pt-2 mt-2">
                          <span className="text-[var(--text-secondary)]">Next payment date</span>
                          <span className="font-bold text-[var(--text-primary)]">
                            {new Date(currentPeriodEnd).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {needsPayment && (
                      <button
                        onClick={() => handlePathBCheckout(coach.id)}
                        disabled={!!actionLoading[coach.id]}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-tangerine text-white font-bold rounded-2xl shadow-md shadow-tangerine/20 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                      >
                        {actionLoading[coach.id] ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <span className="material-symbols-outlined text-lg">credit_card</span>
                        )}
                        {actionLoading[coach.id] ? 'Redirecting...' : `Pay $${price}/mo`}
                      </button>
                    )}

                    {isActive && (
                      <button
                        onClick={handleManageBilling}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-secondary)] font-bold rounded-2xl hover:border-tangerine hover:text-tangerine transition-all"
                      >
                        <span className="material-symbols-outlined text-lg">manage_accounts</span>
                        Manage Billing
                      </button>
                    )}
                  </div>

                  {!isApproved && (
                    <p className="text-xs text-[var(--text-secondary)]/70 text-center mt-4">
                      Your coach hasn't approved you yet. They'll set your billing plan once approved.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientBilling;
